// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { Avatar } from '../../components/Avatar';
import { createTheme } from '../../design-system/theme';
import { useColorScheme } from '../../foundations/colorScheme';
import { callHistoryStrings } from '../strings';
import type { CallHistoryActions, CallHistoryEntry } from '../types';

export interface CallHistoryDetailsPanelProps {
  readonly entry: CallHistoryEntry;
  readonly actions?: CallHistoryActions;
  readonly onClose?: () => void;
}

/** Right-rail details panel. Action callbacks are host-wired (no SDK). */
export const CallHistoryDetailsPanel: React.FC<CallHistoryDetailsPanelProps> = ({
  entry,
  actions,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);
  const { summary } = entry;

  const handleCallBack = useCallback(() => {
    actions?.onCallBack?.(entry);
  }, [actions, entry]);
  const handleMessage = useCallback(() => {
    actions?.onMessage?.(entry);
  }, [actions, entry]);
  const handleViewProfile = useCallback(() => {
    actions?.onViewProfile?.(entry);
  }, [actions, entry]);
  const handleToggleFav = useCallback(() => {
    void actions?.onToggleFavorite?.(summary.id, !summary.isFavorite);
  }, [actions, summary.id, summary.isFavorite]);
  const handleDelete = useCallback(() => {
    void actions?.onDelete?.(summary.id);
  }, [actions, summary.id]);

  return (
    <View
      style={[
        styles.wrap,
        {
          backgroundColor: theme.colors.surface.primary,
          borderLeftColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityLabel={callHistoryStrings.detailsTitle}
    >
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {callHistoryStrings.detailsTitle}
        </Text>
        {onClose ? (
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={callHistoryStrings.close}
            hitSlop={8}
          >
            <Text style={[styles.close, { color: theme.colors.text.secondary }]}>✕</Text>
          </Pressable>
        ) : null}
      </View>

      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.hero}>
          <Avatar
            size="large"
            {...(summary.avatarUri !== null ? { imageUri: summary.avatarUri } : {})}
            initials={summary.displayName}
          />
          <Text style={[styles.name, { color: theme.colors.text.primary }]} numberOfLines={1}>
            {summary.displayName}
          </Text>
          {summary.handle ? (
            <Text style={[styles.handle, { color: theme.colors.text.secondary }]} numberOfLines={1}>
              {summary.handle}
            </Text>
          ) : null}
        </View>

        <View style={styles.metaBlock}>
          <DetailRow
            label={callHistoryStrings.detailsKind}
            value={callHistoryStrings.kind(summary.kind)}
            theme={theme}
          />
          <DetailRow
            label={callHistoryStrings.detailsDirection}
            value={callHistoryStrings.direction(summary.direction)}
            theme={theme}
          />
          <DetailRow
            label={callHistoryStrings.detailsStarted}
            value={new Date(summary.startedAt).toISOString().replace('T', ' ').slice(0, 16)}
            theme={theme}
          />
          <DetailRow
            label={callHistoryStrings.detailsDuration}
            value={callHistoryStrings.formatDuration(summary.durationSeconds)}
            theme={theme}
          />
        </View>

        <View style={styles.actions}>
          {actions?.onCallBack ? (
            <ActionButton
              label={callHistoryStrings.actionCallBack}
              onPress={handleCallBack}
              theme={theme}
            />
          ) : null}
          {actions?.onMessage ? (
            <ActionButton
              label={callHistoryStrings.actionMessage}
              onPress={handleMessage}
              theme={theme}
            />
          ) : null}
          {actions?.onViewProfile ? (
            <ActionButton
              label={callHistoryStrings.actionViewProfile}
              onPress={handleViewProfile}
              theme={theme}
            />
          ) : null}
          {actions?.onToggleFavorite ? (
            <ActionButton
              label={
                summary.isFavorite
                  ? callHistoryStrings.actionRemoveFromFavorites
                  : callHistoryStrings.actionAddToFavorites
              }
              onPress={handleToggleFav}
              theme={theme}
            />
          ) : null}
          {actions?.onDelete ? (
            <ActionButton
              label={callHistoryStrings.actionDelete}
              onPress={handleDelete}
              theme={theme}
              destructive
            />
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
};

interface ThemeShape {
  readonly colors: {
    readonly text: { readonly primary: string; readonly secondary: string };
    readonly border: { readonly secondary: string };
    readonly surface: { readonly secondary: string };
    readonly accent: { readonly rose: string; readonly indigo: string };
  };
}

const DetailRow: React.FC<{
  readonly label: string;
  readonly value: string;
  readonly theme: ThemeShape;
}> = ({ label, value, theme }) => (
  <View style={[styles.detailRow, { borderBottomColor: theme.colors.border.secondary }]}>
    <Text style={[styles.detailLabel, { color: theme.colors.text.secondary }]}>{label}</Text>
    <Text style={[styles.detailValue, { color: theme.colors.text.primary }]}>{value}</Text>
  </View>
);

const ActionButton: React.FC<{
  readonly label: string;
  readonly onPress: () => void;
  readonly theme: ThemeShape;
  readonly destructive?: boolean;
}> = ({ label, onPress, theme, destructive = false }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={({ pressed }) => [
      styles.actionRow,
      {
        backgroundColor: theme.colors.surface.secondary,
        opacity: pressed ? 0.8 : 1,
      },
    ]}
  >
    <Text
      style={[
        styles.actionText,
        { color: destructive ? theme.colors.accent.rose : theme.colors.accent.indigo },
      ]}
    >
      {label}
    </Text>
  </Pressable>
);

const styles = StyleSheet.create({
  wrap: {
    width: 360,
    flex: 1,
    borderLeftWidth: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  } satisfies ViewStyle,
  title: {
    fontSize: 15,
    fontWeight: '600',
  } satisfies TextStyle,
  close: {
    fontSize: 16,
    fontWeight: '600',
  } satisfies TextStyle,
  scroll: {
    paddingVertical: 20,
  } satisfies ViewStyle,
  hero: {
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingBottom: 20,
  } satisfies ViewStyle,
  name: {
    fontSize: 18,
    fontWeight: '700',
  } satisfies TextStyle,
  handle: {
    fontSize: 13,
  } satisfies TextStyle,
  metaBlock: {
    paddingHorizontal: 16,
  } satisfies ViewStyle,
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  detailLabel: {
    fontSize: 13,
  } satisfies TextStyle,
  detailValue: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
  actions: {
    padding: 16,
    gap: 8,
  } satisfies ViewStyle,
  actionRow: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
  } satisfies ViewStyle,
  actionText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
});
